/**
 * Brazilian Tax Breakdown Panel Component
 * Shows detailed breakdown of Brazilian taxes (ICMS, PIS, COFINS, ISS)
 */
import React from 'react';
import { Calculator, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { cn } from '../../lib/utils';
import { formatBRL, formatPercentBR } from '../../lib/currency-br';

interface TaxItem {
  name: string;
  base: number;
  rate: number;
  amount: number;
  description?: string;
}

interface TaxBreakdownPanelProps {
  taxes: {
    icms?: TaxItem;
    pis?: TaxItem;
    cofins?: TaxItem;
    iss?: TaxItem;
    ipi?: TaxItem;
    irrf?: TaxItem;
    inss?: TaxItem;
    csll?: TaxItem;
  };
  subtotal: number;
  total: number;
  showDetails?: boolean;
  collapsible?: boolean;
  className?: string;
}

function TaxRow({ tax, showBase = true }: { tax: TaxItem; showBase?: boolean }) {
  return (
    <div className="flex items-center justify-between py-2 text-sm">
      <div className="flex items-center gap-2">
        <span className="font-medium text-gray-700">{tax.name}</span>
        {tax.description && (
          <span className="text-xs text-gray-400">({tax.description})</span>
        )}
      </div>
      <div className="flex items-center gap-4 text-right">
        {showBase && (
          <span className="text-gray-400 text-xs">
            Base: {formatBRL(tax.base)}
          </span>
        )}
        <span className="text-gray-500 min-w-[50px]">
          {formatPercentBR(tax.rate)}
        </span>
        <span className="font-medium text-gray-900 min-w-[100px]">
          {formatBRL(tax.amount)}
        </span>
      </div>
    </div>
  );
}

export default function TaxBreakdownPanel({
  taxes,
  subtotal,
  total,
  showDetails: _showDetails = true,
  collapsible = true,
  className,
}: TaxBreakdownPanelProps) {
  const [isExpanded, setIsExpanded] = React.useState(!collapsible);

  const totalTaxes = Object.values(taxes).reduce(
    (sum, tax) => sum + (tax?.amount ?? 0),
    0
  );

  // Categorize taxes
  const productTaxes = [taxes.icms, taxes.ipi].filter(Boolean) as TaxItem[];
  const serviceTaxes = [taxes.iss].filter(Boolean) as TaxItem[];
  const socialTaxes = [taxes.pis, taxes.cofins].filter(Boolean) as TaxItem[];
  const withholdingTaxes = [taxes.irrf, taxes.inss, taxes.csll].filter(Boolean) as TaxItem[];

  const hasProductTaxes = productTaxes.length > 0;
  const hasServiceTaxes = serviceTaxes.length > 0;
  const hasSocialTaxes = socialTaxes.length > 0;
  const hasWithholdingTaxes = withholdingTaxes.length > 0;

  return (
    <div className={cn('rounded-lg border border-gray-200 bg-white', className)}>
      {/* Header */}
      <div
        className={cn(
          'flex items-center justify-between px-4 py-3 bg-gray-50 rounded-t-lg',
          collapsible && 'cursor-pointer hover:bg-gray-100 transition-colors'
        )}
        onClick={() => collapsible && setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <Calculator className="h-5 w-5 text-teal-600" />
          <h4 className="font-semibold text-gray-900">Detalhamento de Impostos</h4>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-600">
            Total: <span className="text-teal-600">{formatBRL(totalTaxes)}</span>
          </span>
          {collapsible && (
            isExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )
          )}
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* Product Taxes (ICMS, IPI) */}
          {hasProductTaxes && (
            <div>
              <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                Impostos sobre Produtos
              </h5>
              <div className="divide-y divide-gray-100">
                {productTaxes.map((tax) => (
                  <TaxRow key={tax.name} tax={tax} />
                ))}
              </div>
            </div>
          )}

          {/* Service Taxes (ISS) */}
          {hasServiceTaxes && (
            <div>
              <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                Impostos sobre Serviços
              </h5>
              <div className="divide-y divide-gray-100">
                {serviceTaxes.map((tax) => (
                  <TaxRow key={tax.name} tax={tax} />
                ))}
              </div>
            </div>
          )}

          {/* Social Contributions (PIS, COFINS) */}
          {hasSocialTaxes && (
            <div>
              <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                Contribuições Sociais
              </h5>
              <div className="divide-y divide-gray-100">
                {socialTaxes.map((tax) => (
                  <TaxRow key={tax.name} tax={tax} />
                ))}
              </div>
            </div>
          )}

          {/* Withholding Taxes */}
          {hasWithholdingTaxes && (
            <div>
              <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                Retenções
              </h5>
              <div className="divide-y divide-gray-100">
                {withholdingTaxes.map((tax) => (
                  <TaxRow key={tax.name} tax={tax} />
                ))}
              </div>
            </div>
          )}

          {/* Summary */}
          <div className="border-t pt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Subtotal (sem impostos)</span>
              <span className="text-gray-700">{formatBRL(subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Total de Impostos</span>
              <span className="text-teal-600 font-medium">{formatBRL(totalTaxes)}</span>
            </div>
            <div className="flex justify-between text-base font-semibold pt-2 border-t">
              <span className="text-gray-900">Total da Nota</span>
              <span className="text-gray-900">{formatBRL(total)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Compact Tax Summary for lists
 */
interface TaxSummaryBadgeProps {
  icms?: number;
  pis?: number;
  cofins?: number;
  iss?: number;
  total: number;
  className?: string;
}

export function TaxSummaryBadge({
  icms,
  pis,
  cofins,
  iss,
  total: _total,
  className,
}: TaxSummaryBadgeProps) {
  const taxes = [
    icms && { name: 'ICMS', amount: icms },
    pis && { name: 'PIS', amount: pis },
    cofins && { name: 'COFINS', amount: cofins },
    iss && { name: 'ISS', amount: iss },
  ].filter(Boolean) as { name: string; amount: number }[];

  return (
    <div className={cn('flex items-center gap-2 text-xs', className)}>
      {taxes.slice(0, 2).map((tax) => (
        <span
          key={tax.name}
          className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-600"
        >
          {tax.name}: {formatBRL(tax.amount)}
        </span>
      ))}
      {taxes.length > 2 && (
        <span className="text-gray-400">+{taxes.length - 2} mais</span>
      )}
    </div>
  );
}

/**
 * Tax Widget for Dashboard
 */
interface TaxWidgetProps {
  icmsToCollect: number;
  pisToCollect: number;
  cofinsToCollect: number;
  issRetained: number;
  period: string;
  className?: string;
}

export function TaxWidget({
  icmsToCollect,
  pisToCollect,
  cofinsToCollect,
  issRetained,
  period,
  className,
}: TaxWidgetProps) {
  const totalToCollect = icmsToCollect + pisToCollect + cofinsToCollect;

  return (
    <div className={cn('rounded-xl bg-white p-6 shadow-sm border', className)}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900">Resumo de Impostos</h3>
        <span className="text-sm text-gray-500">{period}</span>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-blue-500" />
            <span className="text-sm text-gray-600">ICMS a Recolher</span>
          </div>
          <span className="font-medium text-gray-900">{formatBRL(icmsToCollect)}</span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-purple-500" />
            <span className="text-sm text-gray-600">PIS/COFINS</span>
          </div>
          <span className="font-medium text-gray-900">
            {formatBRL(pisToCollect + cofinsToCollect)}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-amber-500" />
            <span className="text-sm text-gray-600">ISS Retido</span>
          </div>
          <span className="font-medium text-gray-900">{formatBRL(issRetained)}</span>
        </div>

        <div className="pt-3 border-t">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Total a Recolher</span>
            <span className="text-lg font-semibold text-teal-600">
              {formatBRL(totalToCollect)}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-4 p-3 bg-amber-50 rounded-lg flex items-start gap-2">
        <Info className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-amber-700">
          Valores calculados automaticamente com base nas notas fiscais emitidas.
          Consulte seu contador para confirmação.
        </p>
      </div>
    </div>
  );
}
